import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useScrollToTopOnChange } from '../utils/scroll';
import Sortable from 'sortablejs';
import { MessageSquare, Plus, Edit2, Trash2, RefreshCw, X, Check, GripVertical, Award, Library, Search } from 'lucide-react';
import { instructionAPI, specialityAPI } from '../services/api';
import { useToast, useConfirm } from './UIProvider';
import QuickLabTestsTab from './QuickLabTestsTab';
import Select from './Select';
import useDialog from '../hooks/useDialog';
import useKeyboardReorder from '../hooks/useKeyboardReorder';

// Admin management of the predefined pickers used in prescriptions:
//   • Patient General  — the chips on the prescription's Advice step (e.g. "Take after food").
//   • Medication       — per-medicine advice used in the Medicine tab (e.g. "Take with juice").
//   • Lab & Radiology   — per-test advice attached to lab/radiology orders (e.g. "Fasting required").
//   • Quick Lab Tests  — the quick test chips in the Treatment tab (a different entity, own tab).
// Instructions have an English + Urdu version. Delete is a soft deactivate so historical
// prescriptions that referenced an instruction are unaffected.
//
// Every tab is scoped by speciality: the General scope is what every doctor sees, and a speciality's
// own rows are shown ahead of the general ones in the prescription pickers. Existing rows are all
// General, so nothing changes for a clinic that never configures a speciality list.
const TYPES = [
  { key: 'PATIENT_INSTRUCTION', label: 'Patient General' },
  { key: 'MEDICATION_INSTRUCTION', label: 'Medication' },
  { key: 'LAB_INSTRUCTION', label: 'Lab & Radiology' },
  { key: 'QUICK_LAB_TESTS', label: 'Quick Lab Tests' },
];

// The Quick Lab Tests tab is backed by its own entity, not SystemConfiguration, so it renders a
// dedicated child component instead of the instruction list + Add Instruction modal.
const QUICK_TESTS_TAB = 'QUICK_LAB_TESTS';

// Type-appropriate example hints for the Add/Edit form.
const PLACEHOLDERS = {
  PATIENT_INSTRUCTION: { en: 'e.g. Take complete rest', ur: 'مثلاً مکمل آرام کریں' },
  MEDICATION_INSTRUCTION: { en: 'e.g. Take with a glass of water', ur: 'مثلاً ایک گلاس پانی کے ساتھ لیں' },
  LAB_INSTRUCTION: { en: 'e.g. Fasting required (12 hours)', ur: 'مثلاً بارہ گھنٹے کا فاقہ ضروری' },
};

// Shared grid template for the header + every row so the columns line up across independent grids
// (grip · English · Urdu · status · actions). Fixed widths on grip/status/actions; English & Urdu
// share the remaining space. A div grid (not a <table>) is used so rows are freely transformable for
// the FLIP slide animation — transforms on <tr> are unreliable.
const ROW_GRID = 'grid grid-cols-[2.5rem_1.4fr_1.2fr_8rem_6rem] items-center';

const PatientInstructionsManagement = () => {
  const showToast = useToast();
  const confirm = useConfirm();
  const [type, setType] = useState('PATIENT_INSTRUCTION');
  // The speciality whose lists are being edited. Always a real speciality id once the list loads —
  // the shared "General" scope is not offered here, so every list belongs to a named speciality.
  const [specialityId, setSpecialityId] = useState('');
  const [specialities, setSpecialities] = useState([]);
  const [items, setItems] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // {id?, valueEnglish, valueUrdu, configType, specialityId, isActive} | null
  const [showModal, setShowModal] = useState(false);
  const instructionDialogRef = useDialog(() => { setShowModal(false); setEditing(null); });
  // "Add from library" — every instruction of this type written anywhere in the system, so a
  // speciality can reuse existing wording instead of typing it out a second time.
  const [showLibrary, setShowLibrary] = useState(false);
  const [library, setLibrary] = useState(null); // null = loading
  const [librarySearch, setLibrarySearch] = useState('');
  const [librarySelection, setLibrarySelection] = useState(() => new Set());
  // The library lists the whole catalogue, including entries this scope already holds (flagged by
  // the backend), so it needs paging and its own narrowing controls rather than one long list.
  const [libraryFilter, setLibraryFilter] = useState('all'); // all | available | added
  const [libraryScope, setLibraryScope] = useState('all');   // 'all' or a source speciality name
  const [libraryPage, setLibraryPage] = useState(1);
  const [libraryPageSize, setLibraryPageSize] = useState(10);
  // Inline edit of a library entry. Held here rather than in the row so only one is ever open.
  const [libraryEdit, setLibraryEdit] = useState(null); // { id, valueEnglish, valueUrdu }
  const [libraryEditSaving, setLibraryEditSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  // Ticks are clicks, which the hook cannot see, and the filters are not changes.
  const libraryDialogRef = useDialog(() => setShowLibrary(false), {
    isDirty: () => librarySelection.size > 0 || libraryEdit !== null,
  });
  // Toggling and removing are staged here rather than fired one request at a time, so a screenful of
  // changes commits as one transaction. Keyed by row id: pendingActive holds the intended flag,
  // pendingRemove the rows to take off this list.
  const [pendingActive, setPendingActive] = useState(() => new Map());
  const [pendingRemove, setPendingRemove] = useState(() => new Set());
  const [savingBatch, setSavingBatch] = useState(false);
  // Quick Lab Tests keeps its own data, but its Refresh / Add from Library buttons live in the page
  // header alongside the instruction tabs' so both tabs are driven from the same place.
  const [quickLibraryOpen, setQuickLibraryOpen] = useState(false);
  const [quickNewTestOpen, setQuickNewTestOpen] = useState(false);
  const [quickRefreshKey, setQuickRefreshKey] = useState(0);
  // Flash the scope card on mount and on every scope change, then let it settle. The scope decides
  // where new items are SAVED, not just what's listed, so a silent change is too easy to miss.
  const [scopePulse, setScopePulse] = useState(true);
  useEffect(() => {
    setScopePulse(true);
    const t = setTimeout(() => setScopePulse(false), 3700); // matches 3 x 1.2s in .scope-pulse
    return () => clearTimeout(t);
  }, [specialityId]);
  const isQuickTests = type === QUICK_TESTS_TAB;
  const scopeLabel = specialities.find((s) => s.id === specialityId)?.name || 'this speciality';
  // The library modal's scrolling row list — see QuickLabTestsTab: paging swaps the rows, not the
  // scroller, so the next page would otherwise open at the bottom.
  const libraryListRef = useRef(null);
  // Drag-to-reorder via SortableJS: pressing a row lifts a clone that follows the cursor smoothly
  // (forceFallback), while the other rows glide aside (animation). We persist the final order on drop
  // and revert by reloading on error. Refs let the Sortable callback (created once) read the latest
  // type / items / toast without being re-bound on every render.
  const itemsRef = useRef(null);
  const typeRef = useRef(type);
  const specialityRef = useRef(specialityId);
  const showToastRef = useRef(showToast);
  const sortableRef = useRef(null);
  const saveOrderRef = useRef(null);
  itemsRef.current = items;
  typeRef.current = type;
  specialityRef.current = specialityId;
  showToastRef.current = showToast;
  saveOrderRef.current = (next) => {
    instructionAPI
      .reorder(typeRef.current, next.map((i) => i.id), specialityRef.current)
      .then(() => window.dispatchEvent(new Event('clinic:instructions-changed')))
      .catch(async (err) => {
        showToastRef.current('error', err.message || 'Failed to save order');
        try { const fresh = await instructionAPI.list(typeRef.current, specialityRef.current); setItems((Array.isArray(fresh) ? fresh : []).filter((it) => it.isActive !== false)); } catch { /* ignore */ }
      });
  };

  // Callback ref on the rows container: spin up SortableJS when it mounts, tear it down on unmount
  // (so it re-binds cleanly when the list mounts/unmounts — e.g. switching tabs or reloading).
  const rowsContainerRef = useCallback((node) => {
    if (sortableRef.current) { sortableRef.current.destroy(); sortableRef.current = null; }
    if (!node) return;
    sortableRef.current = Sortable.create(node, {
      animation: 180,
      easing: 'cubic-bezier(0.2, 0, 0, 1)',
      forceFallback: true,    // custom clone (not the native ghost) -> smooth, fully stylable drag
      fallbackTolerance: 4,   // a few px of movement before a drag begins, so plain clicks still register
      filter: 'button',       // don't start a drag from the edit / deactivate buttons
      preventOnFilter: false, // ...but let those buttons still receive their click
      ghostClass: 'si-ghost', // the placeholder gap marking where the row will land
      chosenClass: 'si-chosen',
      dragClass: 'si-drag',   // the floating, "popped" clone
      onEnd: (evt) => {
        const { oldIndex, newIndex } = evt;
        if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
        const current = itemsRef.current || [];
        const next = [...current];
        const [moved] = next.splice(oldIndex, 1);
        next.splice(newIndex, 0, moved);
        setItems(next); // keep React in sync with the DOM move Sortable just made
        saveOrderRef.current(next);
      },
    });
  }, []);

  const handleProps = useKeyboardReorder({
    items: items || [],
    onMove: setItems,
    onCommit: (next) => saveOrderRef.current(next),
    getLabel: (it) => it.valueEnglish,
  });

  const load = useCallback(async () => {
    if (type === QUICK_TESTS_TAB) return; // that tab loads its own data
    setItems(null);
    try {
      const list = await instructionAPI.list(type, specialityId);
      // Switching an instruction off is how it leaves this speciality's list, so a row that comes
      // back inactive is not shown here at all. The endpoint still returns both — other callers
      // want the full picture — but on this screen an inactive row would be a dead entry: it is
      // not served to any doctor, and the way to bring it back is Add from Library, which now
      // offers it again and reactivates this very row.
      const active = (Array.isArray(list) ? list : []).filter((it) => it.isActive !== false);
      setItems(active);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load instructions');
      setItems([]);
    }
  }, [type, specialityId]);

  useEffect(() => { load(); }, [load]);

  // Unsaved toggles belong to the list that was on screen. Switching speciality or tab abandons
  // them rather than carrying ids across to a list they aren't part of.
  useEffect(() => {
    setPendingActive(new Map());
    setPendingRemove(new Set());
  }, [type, specialityId]);

  // Instructions are managed per speciality only — there is no "all specialities" option to pick,
  // so the screen opens on the first speciality rather than on an empty scope.
  useEffect(() => {
    let cancelled = false;
    specialityAPI
      .list()
      .then((list) => {
        if (cancelled) return;
        const rows = Array.isArray(list) ? list : [];
        setSpecialities(rows);
        setSpecialityId((current) => (current || (rows[0]?.id ?? '')));
      })
      .catch(() => { if (!cancelled) setSpecialities([]); });
    return () => { cancelled = true; };
  }, []);

  const openAdd = () => {
    // Default the new row to whichever scope is currently being viewed.
    setEditing({ valueEnglish: '', valueUrdu: '', configType: type, specialityId, isActive: true });
    setShowModal(true);
  };
  const openEdit = (it) => {
    setEditing({
      id: it.id,
      valueEnglish: it.valueEnglish,
      valueUrdu: it.valueUrdu,
      configType: it.configType,
      specialityId: it.speciality?.id || '',
      isActive: it.isActive,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const e = editing;
    if (!e.valueEnglish.trim() || !e.valueUrdu.trim()) {
      showToast('error', 'Both English and Urdu text are required');
      return;
    }
    try {
      const payload = {
        valueEnglish: e.valueEnglish.trim(),
        valueUrdu: e.valueUrdu.trim(),
        type: e.configType,
        specialityId: e.specialityId || '',
      };
      if (e.id) await instructionAPI.update(e.id, { ...payload, isActive: e.isActive });
      else await instructionAPI.create(payload);
      showToast('success', e.id ? 'Instruction updated' : 'Instruction added');
      setShowModal(false);
      setEditing(null);
      // Changing the type or speciality on edit moves the row to another tab or scope, so the
      // current list is reloaded rather than patched in place.
      load();
      window.dispatchEvent(new Event('clinic:instructions-changed'));
    } catch (err) {
      showToast('error', err.message || 'Failed to save instruction');
    }
  };

  const openLibrary = async () => {
    setShowLibrary(true);
    setLibrary(null);
    setLibrarySearch('');
    setLibrarySelection(new Set());
    setLibraryFilter('all');
    setLibraryScope('all');
    setLibraryPage(1);
    try {
      const list = await instructionAPI.library(type, specialityId);
      setLibrary(Array.isArray(list) ? list : []);
    } catch (e) {
      setLibrary([]);
      showToast('error', e.message || 'Failed to load the instruction library');
    }
  };

  // Reloads the picker in place after an edit or delete, keeping the search, filters and page the
  // admin was on. Re-running openLibrary would throw all of that away mid-task.
  const reloadLibraryInPlace = async () => {
    try {
      const list = await instructionAPI.library(type, specialityId);
      setLibrary(Array.isArray(list) ? list : []);
    } catch (e) {
      showToast('error', e.message || 'Failed to reload the library');
    }
  };

  const saveLibraryEdit = async () => {
    const english = (libraryEdit.valueEnglish || '').trim();
    const urdu = (libraryEdit.valueUrdu || '').trim();
    if (!english || !urdu) {
      showToast('error', 'Both English and Urdu text are required');
      return;
    }
    setLibraryEditSaving(true);
    try {
      const res = await instructionAPI.updateLibraryEntry(libraryEdit.id, english, urdu);
      setLibraryEdit(null);
      await reloadLibraryInPlace();
      await load();
      const n = res?.updated ?? 0;
      showToast('success', n > 1 ? `Updated in all ${n} specialities using it` : 'Instruction updated');
    } catch (e) {
      showToast('error', e.message || 'Failed to update the instruction');
    } finally {
      setLibraryEditSaving(false);
    }
  };

  const deleteLibraryEntry = async (entry) => {
    const where = (entry.scopes || []).length
      ? ` It is in use by ${entry.scopes.join(', ')}.`
      : '';
    const ok = await confirm({
      title: 'Delete from the library?',
      message: `"${entry.valueEnglish}" will be removed from the library and from every speciality that uses it.${where} Prescriptions already written are not affected.`,
      confirmText: 'Delete everywhere',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const res = await instructionAPI.deleteLibraryEntry(entry.id);
      await reloadLibraryInPlace();
      await load();
      const n = res?.deleted ?? 0;
      showToast('success', n > 1 ? `Removed from all ${n} specialities` : 'Instruction deleted');
    } catch (e) {
      showToast('error', e.message || 'Failed to delete the instruction');
    }
  };

  const toggleLibraryPick = (id) => {
    setLibrarySelection(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCopyFromLibrary = async () => {
    const ids = [...librarySelection];
    if (ids.length === 0) {
      showToast('error', 'Pick at least one instruction');
      return;
    }
    setCopying(true);
    try {
      const res = await instructionAPI.copy(type, specialityId, ids);
      const copied = res?.copied ?? ids.length;
      showToast('success', `${copied} instruction${copied === 1 ? '' : 's'} added to ${scopeLabel}`);
      setShowLibrary(false);
      load();
      window.dispatchEvent(new Event('clinic:instructions-changed'));
    } catch (err) {
      showToast('error', err.message || 'Failed to add the instructions');
    } finally {
      setCopying(false);
    }
  };

  // Search + the two in-result filters, applied together. Kept separate from paging below so the
  // "N of M" count and the pager always describe the same filtered set.
  const filteredLibrary = (library || []).filter((entry) => {
    if (libraryFilter === 'available' && entry.alreadyAdded) return false;
    if (libraryFilter === 'added' && !entry.alreadyAdded) return false;
    if (libraryScope !== 'all' && !(entry.scopes || []).includes(libraryScope)) return false;
    const q = librarySearch.trim().toLowerCase();
    if (!q) return true;
    return (entry.valueEnglish || '').toLowerCase().includes(q)
      || (entry.valueUrdu || '').includes(librarySearch.trim());
  });

  // Every speciality that wrote at least one entry, for the "From" filter.
  const libraryScopes = [...new Set((library || []).flatMap((e) => e.scopes || []))]
    .sort((a, b) => a.localeCompare(b));

  const libraryTotalPages = Math.max(1, Math.ceil(filteredLibrary.length / libraryPageSize));
  // Clamp rather than reset: deleting the filter that made page 5 valid should land on the last
  // page that still exists, not throw the admin back to page 1.
  const libraryCurrentPage = Math.min(libraryPage, libraryTotalPages);
  useScrollToTopOnChange(
    libraryListRef,
    [libraryCurrentPage, libraryPageSize, librarySearch, libraryFilter, libraryScope],
  );
  const libraryPageStart = (libraryCurrentPage - 1) * libraryPageSize;
  const pagedLibrary = filteredLibrary.slice(libraryPageStart, libraryPageStart + libraryPageSize);
  const libraryAvailableCount = (library || []).filter((e) => !e.alreadyAdded).length;

  // What a row's status will be once the pending changes are saved.
  const effectiveActive = (it) =>
    pendingActive.has(it.id) ? pendingActive.get(it.id) : !!it.isActive;

  // Staged, not saved. Toggling back to where it started drops the entry rather than recording a
  // no-op, so the unsaved count only ever reflects real changes.
  const handleToggleActive = (it) => {
    setPendingActive((prev) => {
      const next = new Map(prev);
      const target = !effectiveActive(it);
      if (target === !!it.isActive) next.delete(it.id);
      else next.set(it.id, target);
      return next;
    });
  };

  const toggleRemove = (it) => {
    setPendingRemove((prev) => {
      const next = new Set(prev);
      if (next.has(it.id)) next.delete(it.id);
      else next.add(it.id);
      return next;
    });
  };

  const discardChanges = () => {
    setPendingActive(new Map());
    setPendingRemove(new Set());
  };

  const pendingCount = pendingActive.size + pendingRemove.size;

  const saveBatch = async () => {
    const removeIds = [...pendingRemove];
    // A staged toggle on a row that is also being removed is moot; the removal wins.
    const activate = [...pendingActive.entries()]
      .filter(([id, on]) => on && !pendingRemove.has(id))
      .map(([id]) => id);
    const deactivate = [...pendingActive.entries()]
      .filter(([id, on]) => !on && !pendingRemove.has(id))
      .map(([id]) => id);

    if (removeIds.length > 0) {
      const names = (items || [])
        .filter((it) => pendingRemove.has(it.id))
        .map((it) => `"${it.valueEnglish}"`);
      const ok = await confirm({
        title: removeIds.length === 1 ? 'Remove from this list?' : `Remove ${removeIds.length} from this list?`,
        message: `${names.join(', ')} will be taken off ${scopeLabel}'s list. The wording stays in the library, so it can be added back at any time, and prescriptions already written are unaffected.`,
        confirmText: 'Save changes',
        tone: 'danger',
      });
      if (!ok) return;
    }

    setSavingBatch(true);
    try {
      const res = await instructionAPI.batch(type, specialityId, {
        activate,
        deactivate,
        remove: removeIds,
      });
      discardChanges();
      await load();
      window.dispatchEvent(new Event('clinic:instructions-changed'));
      const parts = [];
      if (res?.activated) parts.push(`${res.activated} activated`);
      if (res?.deactivated) parts.push(`${res.deactivated} removed from this list`);
      if (res?.removed) parts.push(`${res.removed} deleted`);
      showToast('success', parts.length ? `Saved: ${parts.join(', ')}` : 'Nothing to save');
    } catch (err) {
      showToast('error', err.message || 'Failed to save the changes');
    } finally {
      setSavingBatch(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
                <MessageSquare className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Patient Instructions</h1>
                <p className="text-gray-600">Manage the predefined chips shown in prescriptions (English &amp; Urdu)</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Quick Lab Tests gets the same two controls in the same place as the instruction
                  tabs — only "Add Instruction" is absent, since a brand-new test is created from
                  inside the library modal itself. */}
              {isQuickTests && (
                <>
                  <button
                    onClick={() => setQuickRefreshKey((k) => k + 1)}
                    className="p-3 border border-gray-300 rounded-xl hover:bg-gray-50"
                    title="Refresh"
                  >
                    <RefreshCw className="h-5 w-5 text-gray-600" />
                  </button>
                  <button
                    onClick={() => setQuickLibraryOpen(true)}
                    disabled={!specialityId}
                    className="flex items-center gap-2 px-4 py-2.5 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 disabled:opacity-50 disabled:hover:bg-transparent"
                    title={specialityId ? 'Pick tests from the shared catalogue' : 'Pick a speciality first'}
                  >
                    <Library className="h-4 w-4" /> Add from Library
                  </button>
                  {/* Mirrors "Add Instruction": creates a test the catalogue has never carried and
                      puts it straight on this speciality's list. */}
                  <button
                    onClick={() => setQuickNewTestOpen(true)}
                    disabled={!specialityId}
                    className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                    title={specialityId ? undefined : 'Pick a speciality first'}
                  >
                    <Plus className="h-4 w-4" /> Add Test
                  </button>
                </>
              )}
              {!isQuickTests && (
                <>
                  <button onClick={load} className="p-3 border border-gray-300 rounded-xl hover:bg-gray-50" title="Refresh">
                    <RefreshCw className="h-5 w-5 text-gray-600" />
                  </button>
                  {/* Both disabled until a speciality is chosen: every list belongs to one, and
                      saving with no scope would write to the shared General list this screen no
                      longer manages. */}
                  <button
                    onClick={openLibrary}
                    disabled={!specialityId}
                    className="flex items-center gap-2 px-4 py-2.5 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 disabled:opacity-50 disabled:hover:bg-transparent"
                    title={specialityId ? 'Reuse an instruction already written elsewhere in the system' : 'Pick a speciality first'}
                  >
                    <Library className="h-4 w-4" /> Add from Library
                  </button>
                  <button
                    onClick={openAdd}
                    disabled={!specialityId}
                    className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                    title={specialityId ? undefined : 'Pick a speciality first'}
                  >
                    <Plus className="h-4 w-4" /> Add Instruction
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Speciality scope — deliberately ABOVE the tabs, because it scopes all four of them.
            This is not just a filter: it is also the destination for anything added, so the copy
            leads with that and the card pulses briefly whenever the scope changes. */}
        <div
          className={`rounded-xl border-2 p-4 mb-4 transition-colors ${scopePulse ? 'scope-pulse' : ''} ${
            specialityId ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300 bg-white'
          }`}
        >
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${specialityId ? 'bg-emerald-600' : 'bg-gray-400'}`}>
              <Award className="h-5 w-5 text-white" />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="scope-speciality" className="text-sm font-semibold text-gray-700">Speciality</label>
              <Select
                id="scope-speciality"
                value={specialityId}
                onChange={setSpecialityId}
                options={specialities.map((sp) => ({ value: sp.id, label: sp.name }))}
                placeholder={specialities.length === 0 ? 'No specialities yet' : 'Select a speciality'}
                className="min-w-[16rem]"
                buttonClassName={'p-2.5 border-2 font-medium ' + (specialityId ? 'border-emerald-500' : '')}
              />
            </div>
            <div className="flex-1 min-w-[19rem]">
              <p className={`text-sm font-bold ${specialityId ? 'text-emerald-800' : 'text-gray-800'}`}>
                {specialityId
                  ? `You are editing ${scopeLabel}'s own lists`
                  : 'No speciality selected'}
              </p>
              <p className="text-xs text-gray-600 mt-0.5">
                {specialityId
                  ? `Everything you add on the tabs below is saved to ${scopeLabel} only. Its doctors see these first, then the shared ones.`
                  : 'Add a speciality under Settings → Specialities, then come back to set up its lists.'}
              </p>
            </div>
          </div>
        </div>

        {/* Type tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => setType(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                type === t.key ? 'bg-navy-800 text-white border-navy-800' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {isQuickTests ? (
          <QuickLabTestsTab
            specialityId={specialityId}
            scopeLabel={scopeLabel}
            libraryOpen={quickLibraryOpen}
            onLibraryClose={() => setQuickLibraryOpen(false)}
            newTestOpen={quickNewTestOpen}
            onNewTestClose={() => setQuickNewTestOpen(false)}
            refreshKey={quickRefreshKey}
          />
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg text-red-700">{error}</div>
        ) : items === null ? (
          <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-500">
              {specialityId ? `No instructions for ${scopeLabel}` : 'No instructions yet'}
            </h3>
            <p className="text-gray-400 text-sm mt-1">Click "Add Instruction" to create one.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-2.5 bg-emerald-50/60 border-b border-emerald-100 text-xs text-emerald-700">
              <GripVertical className="h-4 w-4 flex-shrink-0" />
              Drag rows, or use the arrow keys on a row's handle, to set the order these instructions appear in prescriptions.
            </div>
            {/* Column header */}
            <div className={`${ROW_GRID} bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider`}>
              <div className="px-2 py-3"><span className="sr-only">Reorder</span></div>
              <div className="px-6 py-3 text-left">English</div>
              <div className="px-6 py-3 text-right">Urdu</div>
              <div className="px-4 py-3 text-left">Status</div>
              <div className="px-4 py-3 text-right">Actions</div>
            </div>
            {/* Rows - SortableJS reorders the children of this container; plain div grid (not a table)
                so the dragged clone styles cleanly. */}
            <div ref={rowsContainerRef}>
              {items.map((it, index) => {
                const active = effectiveActive(it);
                const removing = pendingRemove.has(it.id);
                const changed = pendingActive.has(it.id) || removing;
                return (
                <div
                  key={it.id}
                  className={`si-row ${ROW_GRID} border-b border-gray-100 last:border-b-0 cursor-grab ${
                    removing
                      ? 'bg-rose-50/70'
                      : changed
                        ? 'bg-amber-50/60'
                        : 'bg-white hover:bg-gray-50/60'
                  } ${!active && !removing ? 'opacity-60' : ''}`}
                >
                  <div className="px-2 py-3 flex justify-center">
                    <span {...handleProps(it, index)} className="inline-flex p-1 rounded text-gray-400 hover:text-gray-600 cursor-grab focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"><GripVertical className="h-4 w-4" /></span>
                  </div>
                  <div className={`px-6 py-3 text-sm ${removing ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                    {it.valueEnglish}
                  </div>
                  <div className={`px-6 py-3 text-lg text-right urdu-text ${removing ? 'text-gray-400 line-through' : 'text-gray-800'}`} dir="rtl">{it.valueUrdu}</div>
                  <div className="px-4 py-3">
                    {/* Status is staged, not saved — nothing reaches the server until Save changes. */}
                    {removing ? (
                      <span className="text-xs font-medium text-rose-700">Removing</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={active}
                          onClick={() => handleToggleActive(it)}
                          title={active
                            ? 'Active - switch off to take it off this list (it stays in the library)'
                            : 'Switched off - it leaves this list when you save'}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 ${active ? 'bg-emerald-500' : 'bg-gray-300'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${active ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                        <span className={`text-xs font-medium ${active ? 'text-emerald-700' : 'text-gray-400'}`}>
                          {active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {removing ? (
                        <button
                          onClick={() => toggleRemove(it)}
                          className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                          title="Keep it on this list"
                        >
                          Undo
                        </button>
                      ) : (
                        <>
                          <button onClick={() => openEdit(it)} className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Edit">
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => toggleRemove(it)}
                            className="p-2 text-gray-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg"
                            title={`Remove from ${scopeLabel}'s list (stays in the library)`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Unsaved changes bar. Every toggle and removal on this list commits together in one
          transaction, so a half-applied list is not a state you can end up in. */}
      {pendingCount > 0 && (
        <div className="sticky bottom-0 z-30 mt-4 -mx-6 px-6 pb-4">
          <div className="max-w-6xl mx-auto bg-white border border-amber-300 shadow-xl rounded-2xl px-5 py-3 flex items-center gap-4 flex-wrap">
            <span className="flex-1 min-w-[12rem] text-sm text-gray-700">
              <span className="font-semibold text-amber-700">
                {pendingCount} unsaved {pendingCount === 1 ? 'change' : 'changes'}
              </span>
              {(() => {
                const on = [...pendingActive.values()].filter((v) => v === true).length;
                const off = [...pendingActive.values()].filter((v) => v === false).length;
                const bits = [];
                if (on) bits.push(`${on} to activate`);
                // Switching off takes the row off this list (it returns to the library), so the
                // summary says that rather than "deactivate", which sounded like it would stay
                // put and merely be greyed out.
                if (off) bits.push(`${off} to remove from this list`);
                if (pendingRemove.size) bits.push(`${pendingRemove.size} to delete`);
                return bits.length ? <span className="text-gray-500"> · {bits.join(', ')}</span> : null;
              })()}
            </span>
            <button
              type="button"
              onClick={discardChanges}
              disabled={savingBatch}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={saveBatch}
              disabled={savingBatch}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {savingBatch ? 'Saving' : 'Save changes'}
            </button>
          </div>
        </div>
      )}

      {/* Add / Edit modal */}
      {showModal && editing && (
        <div ref={instructionDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) { setShowModal(false); setEditing(null); } }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{editing.id ? 'Edit Instruction' : 'Add Instruction'}</h3>
              <button aria-label="Close" onClick={() => { setShowModal(false); setEditing(null); }} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Adding always lands in the list you are looking at — same rule as the Quick Lab
                  Tests tab, so both flows behave identically. Only editing exposes the pickers,
                  because moving an existing item somewhere else is a deliberate, separate action. */}
              {!editing.id ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3.5 py-3 text-sm text-emerald-900">
                  Adding a <span className="font-semibold">{(TYPES.find((t) => t.key === editing.configType) || {}).label}</span> instruction for{' '}
                  <span className="font-semibold">{scopeLabel}</span>.
                  <span className="block text-xs text-emerald-700/90 mt-1">
                    Change the tab or the speciality above to add it somewhere else.
                  </span>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type <span className="text-red-500">*</span></label>
                    <Select
                      value={editing.configType}
                      onChange={(v) => setEditing({ ...editing, configType: v })}
                      options={TYPES.filter((t) => t.key !== QUICK_TESTS_TAB).map((t) => ({ value: t.key, label: t.label }))}
                      buttonClassName="p-2.5 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Speciality</label>
                    {/* Same rule as the scope picker above: an instruction is moved between
                        specialities, never back to the shared General list. An older row that
                        still sits in General keeps its value here so editing it doesn't silently
                        reassign it. */}
                    <Select
                      value={editing.specialityId || ''}
                      onChange={(v) => setEditing({ ...editing, specialityId: v })}
                      options={[
                        ...(editing.specialityId ? [] : [{ value: '', label: 'General (shared list)' }]),
                        ...specialities.map((sp) => ({ value: sp.id, label: sp.name })),
                      ]}
                      buttonClassName="p-2.5 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                      Changing the type or speciality moves this instruction to that list.
                    </p>
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">English <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={editing.valueEnglish}
                  onChange={(e) => setEditing({ ...editing, valueEnglish: e.target.value })}
                  placeholder={(PLACEHOLDERS[editing.configType] || PLACEHOLDERS.PATIENT_INSTRUCTION).en}
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Urdu <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  dir="rtl"
                  value={editing.valueUrdu}
                  onChange={(e) => setEditing({ ...editing, valueUrdu: e.target.value })}
                  placeholder={(PLACEHOLDERS[editing.configType] || PLACEHOLDERS.PATIENT_INSTRUCTION).ur}
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-lg urdu-text text-right"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button onClick={() => { setShowModal(false); setEditing(null); }} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={handleSave} className="px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 flex items-center gap-2">
                <Check className="h-4 w-4" /> {editing.id ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Library — every instruction written anywhere in the system, browsable as a paged
          catalogue. Picking one copies its wording into the current scope as its own row, so
          editing it later never touches the original. Entries this scope already holds are listed
          too (greyed and unpickable) rather than hidden, so a fully-stocked scope doesn't look
          like an empty library. */}
      {showLibrary && (
        <div ref={libraryDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) setShowLibrary(false); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Add from Library</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Instructions written under any speciality. The ones you pick are added to <span className="font-medium text-gray-700">{scopeLabel}</span>.
                </p>
              </div>
              <button aria-label="Close" onClick={() => setShowLibrary(false)} className="p-2 hover:bg-gray-100 rounded-lg flex-none"><X className="w-5 h-5" /></button>
            </div>

            <div className="px-6 pt-4 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={librarySearch}
                  onChange={(e) => { setLibrarySearch(e.target.value); setLibraryPage(1); }}
                  placeholder="Search the library"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-[11rem]">
                  <Select
                    value={libraryFilter}
                    onChange={(v) => { setLibraryFilter(v); setLibraryPage(1); }}
                    options={[
                      { value: 'all', label: `All (${(library || []).length})` },
                      { value: 'available', label: `Not added yet (${libraryAvailableCount})` },
                      { value: 'added', label: `Already added (${(library || []).length - libraryAvailableCount})` },
                    ]}
                    buttonClassName="px-3 py-2"
                  />
                </div>
                {libraryScopes.length > 1 && (
                  <div className="min-w-[11rem]">
                    <Select
                      value={libraryScope}
                      onChange={(v) => { setLibraryScope(v); setLibraryPage(1); }}
                      options={[
                        { value: 'all', label: 'From any speciality' },
                        ...libraryScopes.map((s) => ({ value: s, label: `From ${s}` })),
                      ]}
                      buttonClassName="px-3 py-2"
                    />
                  </div>
                )}
                <span className="text-sm text-gray-500 ml-auto">
                  {filteredLibrary.length} of {(library || []).length} shown
                </span>
              </div>
            </div>

            <div ref={libraryListRef} className="flex-1 overflow-y-auto px-6 py-4">
              {library === null ? (
                <div className="flex justify-center py-12"><div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div>
              ) : filteredLibrary.length === 0 ? (
                <div className="text-center py-12 text-gray-400 text-sm">
                  {library.length === 0
                    ? 'The library is empty. Instructions written under any speciality appear here.'
                    : 'Nothing in the library matches that search.'}
                </div>
              ) : (
                <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
                  {pagedLibrary.map((entry) => {
                    const picked = librarySelection.has(entry.id);
                    // Entries this scope already holds are listed so the catalogue is complete, but
                    // they can't be picked — copying one would be a no-op the backend skips anyway.
                    const added = !!entry.alreadyAdded;
                    const isEditing = libraryEdit?.id === entry.id;

                    if (isEditing) {
                      return (
                        <div key={entry.id} className="px-4 py-3 bg-emerald-50/60 border-l-2 border-emerald-500">
                          <p className="text-[11px] text-gray-500 mb-2">
                            Editing changes this instruction everywhere it is used
                            {(entry.scopes || []).length > 0 && `, including ${entry.scopes.join(', ')}`}.
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <input
                              autoFocus
                              value={libraryEdit.valueEnglish}
                              onChange={(e) => setLibraryEdit({ ...libraryEdit, valueEnglish: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              placeholder="English"
                            />
                            <input
                              value={libraryEdit.valueUrdu}
                              onChange={(e) => setLibraryEdit({ ...libraryEdit, valueUrdu: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg text-lg urdu-text text-right"
                              dir="rtl"
                              placeholder="اردو"
                            />
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              type="button"
                              onClick={saveLibraryEdit}
                              disabled={libraryEditSaving}
                              className="px-4 py-1.5 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                            >
                              {libraryEditSaving ? 'Saving' : 'Save everywhere'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setLibraryEdit(null)}
                              className="px-4 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={entry.id}
                        className={`flex items-start gap-3 px-4 py-3 transition-colors ${
                          added ? 'bg-gray-50' : picked ? 'bg-emerald-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        {/* The pick target is its own button so the edit/delete controls can sit
                            beside it — a button cannot legally contain other buttons. */}
                        <button
                          type="button"
                          disabled={added}
                          onClick={() => toggleLibraryPick(entry.id)}
                          className={`flex items-start gap-3 text-left min-w-0 flex-1 ${added ? 'cursor-not-allowed' : ''}`}
                        >
                          <span className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-none ${
                            added ? 'bg-gray-200 border-gray-200' : picked ? 'bg-emerald-600 border-emerald-600' : 'border-gray-300 bg-white'
                          }`}>
                            {(picked || added) && <Check className={`w-3.5 h-3.5 ${added ? 'text-gray-500' : 'text-white'}`} />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className={`block text-sm ${added ? 'text-gray-500' : 'text-gray-900'}`}>{entry.valueEnglish}</span>
                            <span className="block text-[11px] text-gray-400 mt-0.5">
                              {(entry.scopes || []).length > 0
                                ? `In use by ${(entry.scopes || []).join(', ')}`
                                : 'Not in use anywhere right now'}
                              {added && <span className="ml-2 text-emerald-700 font-medium">Already on this list</span>}
                            </span>
                          </span>
                        </button>
                        <span className={`urdu-text text-lg text-right flex-none max-w-[35%] ${added ? 'text-gray-400' : 'text-gray-700'}`} dir="rtl">{entry.valueUrdu}</span>
                        <span className="flex items-center gap-1 flex-none">
                          <button
                            type="button"
                            onClick={() => setLibraryEdit({ id: entry.id, valueEnglish: entry.valueEnglish, valueUrdu: entry.valueUrdu || '' })}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Edit everywhere it is used"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteLibraryEntry(entry)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-rose-700 hover:bg-rose-50"
                            title="Delete from the library and every speciality"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {library !== null && filteredLibrary.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Rows per page</span>
                  <div className="w-24">
                    <Select
                      value={libraryPageSize}
                      onChange={(v) => { setLibraryPageSize(Number(v)); setLibraryPage(1); }}
                      options={[10, 25, 50, 100].map((n) => ({ value: n, label: String(n) }))}
                      buttonClassName="px-3 py-1.5"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">
                    {libraryPageStart + 1}–{Math.min(libraryPageStart + libraryPageSize, filteredLibrary.length)} of {filteredLibrary.length}
                  </span>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setLibraryPage(Math.max(1, libraryCurrentPage - 1))}
                      disabled={libraryCurrentPage <= 1}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      onClick={() => setLibraryPage(Math.min(libraryTotalPages, libraryCurrentPage + 1))}
                      disabled={libraryCurrentPage >= libraryTotalPages}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <span className="text-sm text-gray-500">
                {librarySelection.size > 0 ? `${librarySelection.size} selected` : 'Nothing selected yet'}
              </span>
              <div className="flex gap-3">
                <button onClick={() => setShowLibrary(false)} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
                <button
                  onClick={handleCopyFromLibrary}
                  disabled={copying || librarySelection.size === 0}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />{copying ? 'Adding...' : `Add to ${scopeLabel}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientInstructionsManagement;
